import { AlertBar } from '#GameFrontend/components/ui/AlertBar/AlertBar';
import { Button } from '#GameFrontend/components/ui/button';
import { Input } from '#GameFrontend/components/ui/input';
import { Label } from '#GameFrontend/components/ui/label';
import { socket } from '#GameFrontend/services/socket';
import { RuleSet, validate as validatePassword } from '#GameFrontend/unauthorized/lib/Validation';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

const PasswordReset = () => {
	const navigate = useNavigate();
	const { emailIdentifier, hash } = useParams();
	const { t } = useTranslation();
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [success, setSuccess] = useState<string | null>(null);
	const [isAlertVisible, setIsAlertVisible] = useState<boolean>(false);
	const ruleSet = useRef<{ [key: string]: RuleSet } | undefined>(undefined);

	useEffect(() => {
		// Get validation configuration from server
		socket.timeout(5000).emit('User:Create:ValidationConfig', {});

		socket.on('User:Create:ValidationConfig', (data) => {
			if (data.success) {
				ruleSet.current = data.data as { [key: string]: RuleSet };
			} else {
				setError(t('load.config.error'));
				setIsAlertVisible(true);
			}
		});

		socket.on('User:PasswordReset', (data) => {
			if (data.success) {
				setError(null);
				setSuccess(t('password.reset.success'));
				setIsAlertVisible(true);
				// Redirect to login page after 3 seconds
				setTimeout(() => {
					navigate('/');
				}, 3000);
			} else {
				if (data.translation.length > 0) {
					const translation = data.translation.map(
						(translation: { key: string; params: Record<string, string> }) =>
							t(translation.key, translation.params)
					);
					setError(translation.join('\n'));
				} else {
					setError(data.message);
				}
				setIsAlertVisible(true);
			}
		});

		return () => {
			socket.off('User:Create:ValidationConfig');
			socket.off('User:PasswordReset');
		};
	}, [navigate, t]);

	const validate = () => {
		if (!ruleSet.current) {
			setError(t('load.config.error'));
			setIsAlertVisible(true);
			return false;
		}

		const errorList = [];

		// Validate password using the rule set
		const passwordValidation = validatePassword('password', ruleSet.current.password, password);
		if (!passwordValidation.valid) {
			errorList.push(passwordValidation.message);
		}

		// Additional validation for password confirmation
		if (password !== confirmPassword) {
			errorList.push(t('validation.missmatch'));
		}

		if (errorList.length > 0) {
			setError(errorList.join('\n'));
			setIsAlertVisible(true);
			return false;
		}

		return true;
	};

	const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		if (!validate()) {
			return;
		}

		setIsLoading(true);
		socket.timeout(5000).emit(
			'User:PasswordReset',
			{
				emailIdentifier,
				hash,
				password,
			},
			() => {
				setIsLoading(false);
			}
		);
	};

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;

		if (name === 'password') {
			setPassword(value);
		} else if (name === 'confirmPassword') {
			setConfirmPassword(value);
		}

		// Clear error when input changes
		setError(null);
		setIsAlertVisible(false);
	};

	return (
		<>
			{success && (
				<AlertBar
					type="success"
					message={success}
					isVisible={isAlertVisible}
					setIsVisible={setIsAlertVisible}
				/>
			)}
			{error && (
				<AlertBar type="error" message={error} isVisible={isAlertVisible} setIsVisible={setIsAlertVisible} />
			)}
			<div>
				<form className="relative" onSubmit={handleSubmit}>
					<div className="wood-light p-6 pb-6 rounded-[15px] shadow-md">
						<div className="space-y-2 relative">
							<Label htmlFor="password" className="text-white text-lg">
								{t('password.reset.new_password')}
							</Label>
							<Input
								id="password"
								name="password"
								type="password"
								value={password}
								onChange={handleInputChange}
								className="bg-white"
							/>
						</div>
						<div className="space-y-2 relative mt-2">
							<Label htmlFor="confirmPassword" className="text-white text-lg">
								{t('password.reset.confirm_password')}
							</Label>
							<Input
								id="confirmPassword"
								name="confirmPassword"
								type="password"
								value={confirmPassword}
								onChange={handleInputChange}
								className="bg-white"
							/>
						</div>

						<div className="flex justify-center lg:hidden space-y-2 mt-4 relative">
							<Button
								disabled={isLoading}
								className="w-full lg:w-[120px] orange-solid-button rounded-[10px] lg:text-base shadow-md text-lg"
							>
								{isLoading ? t('password.reset.loading') : t('password.reset.button')}
							</Button>
						</div>
					</div>
					<div className="hidden lg:flex justify-center mt-0 pt-0">
						<Button
							disabled={isLoading}
							className="w-full lg:w-[120px] orange-solid-button rounded-[10px] lg:text-base shadow-md rounded-t-none border-t-0"
						>
							{isLoading ? t('password.reset.loading') : t('password.reset.button')}
						</Button>
					</div>
				</form>
			</div>
		</>
	);
};

export default PasswordReset;
